/*
*   @site           Preloaded: Site
*   @function       Module: Carousel Template
*   @author         Last17
*   @copyright      Copyright 2025, Preloaded.com
*   @version        0.01
*
*********************************************************************************************/


//-----------------------------------------------
// Imports
//-----------------------------------------------
import ModuleTemplate from './ModuleTemplate';
import Hammer from 'hammerjs';

//-----------------------------------------------
// Class Definition
//-----------------------------------------------
export default class ModuleCarousel extends ModuleTemplate {

  //-----------------------------------------------
  // Init
  //-----------------------------------------------
  init() {
    //Config
    this.config = {
      autoplay: {
        active: (this.element.getAttribute('data-autoplay') !== undefined && this.element.getAttribute('data-autoplay') === '1') ? true : false,
        haltOnInteraction: (this.element.getAttribute('data-autoplay-restart') !== undefined && this.element.getAttribute('data-autoplay-restart') === '1') ? true : false,
        speed: (this.element.getAttribute('data-autoplay-speed') !== undefined) ? parseInt(this.element.getAttribute('data-autoplay-speed')) : 5000,
        loop: (this.element.getAttribute('data-autoplay') !== undefined && (this.element.getAttribute('data-autoplay') === '' || this.element.getAttribute('data-autoplay') === '0')) ? false : true,
      },
      items:{max:-1, current:-1}
    };

    //Values
    this.slides = [];
    this.nodes = [];

    //Init the slides
    this.slidesInit();

    //Construct the navigation
    if(this.config.items.max > 0) {
      this.navigationInit();
    }
    
    //Listen to events (swipes and media play events)
    this.eventsInit();

    //Init a first item
    if(this.config.items.max >= 0) {
      this.slideSet(this.carousel.getAttribute('data-item-first') || 0, false, true);
    }

    //Handle auto play
    if(this.config.autoplay.active) {
      this.autoplayTimer = setInterval(function() {
        this.autoplayTick();
      }.bind(this), this.config.autoplay.speed);
    }
  }

  //-----------------------------------------------
  // Set layout
  //-----------------------------------------------
  setLayout(layout) {
    this.setHeight();
  }


  //-----------------------------------------------
  // Custom resize
  //-----------------------------------------------
  onResize(data) {
    if(!data.changed.layout) { //no need to repeat if setLayout got called
      this.setHeight();
    }
  }


  //-----------------------------------------------
  // Set height
  //-----------------------------------------------
  setHeight() {
    if(this.track) {
      //Find the highsest slide and use that to size the carosel
      let height = 0;
      for(let i = 0; i < this.slides.length; i++) {
        height = Math.max(height, this.slides[i].element.offsetHeight);
      }
      
      //Set the height
      this.track.style.height = height + 'px';
    }
  }

  
  //-----------------------------------------------
  // Slides Init
  //-----------------------------------------------
  slidesInit() {
    //Find the base element etc.
    this.carousel = this.element.querySelector('.carousel');
   
    if(this.carousel) {
      this.track = this.carousel.querySelector('.carousel-track');
      let slides = this.carousel.querySelectorAll('.slide');
      let slideData;
      for(let i = 0; i < slides.length; i++) {
        //Process the slide
        slideData = {
          element: slides[i],
          type: slides[i].getAttribute('data-type') || 'standard',
          content: {}
        };

        slideData.content = this.slideContentInit(i, slides[i], slideData.type);

        //Add to the list
        this.slides.push(slideData);
      }

      //Max and start item
      this.config.items.max = this.slides.length - 1;
    }
  }


  //-----------------------------------------------
  // Slide Content Init (override)
  //-----------------------------------------------
  slideContentInit(id, element, type) {
    return {};
  }


  //-----------------------------------------------
  // Navigation Init
  //-----------------------------------------------
  navigationInit() {
    //Wrapper
    let wrapper = document.createElement('ul');
    wrapper.classList.add('carousel-navigation');

    //Classes
    if(this.element.getAttribute('data-navigation-class')) {
      let classes = this.element.getAttribute('data-navigation-class').split(',');
      for(let i = 0; i < classes.length; i++) {
        wrapper.classList.add(classes[i]);
      }
    }

    //Nodes
    let node;
    for(let i = 0; i <= this.config.items.max; i++) {
      node = document.createElement('li');
      node.classList.add('carousel-navigation__node');
      this.nodes.push(node);
      wrapper.appendChild(node);

      //Event
      node.setAttribute('slideID', i);
      node.addEventListener('click', (event) => {
        this.slideSet(parseInt(event.target.getAttribute('slideID')));
      });
    }

    //Add it!
    this.carousel.appendChild(wrapper);
  }

  //-----------------------------------------------
  // Events Init
  //-----------------------------------------------
  eventsInit() {
    if(this.config.items.max > 0 && this.track) {
      //Listen for swipe on the track
      this.gestures = new Hammer(this.track);
      this.gestures.on('swipe', (e) => {
        this.slideSwipe(e.direction);
      });
    }
  }


  //-----------------------------------------------
  // Slide Init
  //-----------------------------------------------
  slideSet(id, fromUserAction, isFirstRun) {
    //Defaults
    isFirstRun = (isFirstRun === undefined) ? false : isFirstRun;
    fromUserAction = (fromUserAction === undefined) ? true : fromUserAction;
    
    //Only act if not the current slide
    if(id !== this.config.items.current) {
      //Remove old?
      if(this.config.items.current >= 0) {
        this.itemActive(this.config.items.current, false, isFirstRun);
      }

      //New on
      this.itemActive(id, true, isFirstRun);
      this.config.items.current = id;
    }

    //Autoplay
    if(!isFirstRun && this.autoplayTimer && !this.config.autoplay.haltOnInteraction && fromUserAction) {
      this.autoplayKill();
    }
  }


  //-----------------------------------------------
  // Slide Swipe
  //-----------------------------------------------
  slideSwipe(direction) {
    if(direction === Hammer.DIRECTION_LEFT) {
      this.slideSet(Math.min(this.config.items.max, this.config.items.current + 1));
    } else if(direction === Hammer.DIRECTION_RIGHT) {
      this.slideSet(Math.max(0, this.config.items.current - 1));
    }
  }


  //-----------------------------------------------
  // Item Acrtive
  //-----------------------------------------------
  itemActive(id, active, isFirstRun) {
    if(active) {
      //On
      this.slides[id].element.classList.add('active');
      (this.nodes[id]) ? this.nodes[id].classList.add('selected') : null;
      
    } else {
      //Off
      this.slides[id].element.classList.remove('active');
      (this.nodes[id]) ? this.nodes[id].classList.remove('selected') : null;

      //Handle any media type (i.e. halting a playing video)
      if(this.slides[id].type === 'video' && this.slides[id].content.player) {
        this.slides[id].content.player.pause();
      }
    }
  }

  //-----------------------------------------------
  // Autoplay: Tick
  //-----------------------------------------------
  autoplayTick() {
    //Calculate the next slide
    let next = this.config.items.current + 1; //Math.min(this.config.items.max, this.config.items.current + 1)
    if(next <= this.config.items.max) {
      //Move to the next slide
      this.slideSet(next, false);
    } else {
      //Reached the end, so loop, or stop
      (this.config.autoplay.loop) ? this.slideSet(0, false) : this.autoplayKill();
    }
  }

  //-----------------------------------------------
  // Autoplay: Kill
  //-----------------------------------------------
  autoplayKill() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
    console.log('killed the timer');
  }
}